import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, Prompt } from 'react-router-dom'
import { assignScanProtocolToJob, deleteScanProtocol, fetchCustomers, fetchScanProtocolQueue, fetchScanJobCandidates, retryScanProtocol } from '../lib/crmClient'
import { getEmployeeByEmail } from '../lib/technicians'
import { getProtocolJobDate } from '../lib/scanProtocol'

const normalizeText = (value) => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase()

const formatDate = (value) => {
  if (!value) return '-'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return String(value)
  return parsed.toLocaleDateString('cs-CZ')
}

const formatDateTime = (value) => {
  if (!value) return '-'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return String(value)
  return parsed.toLocaleString('cs-CZ', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatMoney = (value) => {
  if (value === null || value === undefined || value === '') return '-'
  const amount = Number(value)
  if (!Number.isFinite(amount)) return '-'
  return `${amount.toLocaleString('cs-CZ')} Kč`
}

const getStatusLabel = (value) => {
  const status = normalizeText(value)
  if (status === 'processed') return 'Zpracováno'
  if (status === 'invalid') return 'Neplatný scan'
  if (status === 'failed') return 'Chyba'
  if (status === 'processing') return 'Zpracovává se'
  if (status === 'queued' || status === 'pending') return 'Ve frontě'
  return value || 'Bez stavu'
}

const buildSearchHaystack = (customer) => {
  const parts = [
    customer.Nazev,
    customer.Ulice,
    customer.Mesto,
    customer.ZakaznikFA,
    customer.UliceFA,
    customer.KontaktniOsoba,
    customer.Telefon,
    customer.Email,
    customer.IC,
    customer.DIC,
  ]

  return normalizeText(parts.filter(Boolean).join(' | '))
}

const getCandidateScore = (customer, query, selectedId, suggestedId) => {
  if (!customer) return -1
  let score = 0
  if (selectedId && Number(customer.ZakaznikId) === Number(selectedId)) score += 500
  if (suggestedId && Number(customer.ZakaznikId) === Number(suggestedId)) score += 250
  if (!query) return score

  const haystack = buildSearchHaystack(customer)
  if (!haystack) return score
  if (!query.split(/\s+/).every((token) => haystack.includes(token))) return -1
  if (haystack === query) score += 400
  if (haystack.startsWith(query)) score += 200
  if (haystack.includes(query)) score += 100

  const name = normalizeText(customer.Nazev)
  if (name === query) score += 600
  if (name.startsWith(query)) score += 350
  if (name.includes(query)) score += 200
  return score
}

const buildDisplayTitle = (item) => {
  const protocolNumber = item.DetectedProtocolNumber || item.detectedProtocolNumber
  const customerName = item.SuggestedZakaznikName || item.suggestedZakaznikName || item.DetectedCustomerName || item.detectedCustomerName
  if (protocolNumber && customerName) return `Protokol ${protocolNumber} • ${customerName}`
  if (protocolNumber) return `Protokol ${protocolNumber}`
  if (customerName) return customerName
  return 'Naskenovaný protokol'
}

const getUploaderLabel = (userEmail) => {
  const employee = getEmployeeByEmail(userEmail)
  return employee ? employee.fullName : (userEmail || '-')
}

const getAssignedJobId = (item) => item.AssignedZakazkaId || item.assignedZakazkaId || null

const pickProtocolDate = getProtocolJobDate

const buildInitialItem = (item, customerMap) => {
  const suggestedId = Number(item.SuggestedZakaznikId || item.suggestedZakaznikId || 0)
  const suggestedCustomer = suggestedId ? customerMap.get(suggestedId) : null
  const initialSearch = suggestedCustomer?.Nazev || item.SuggestedZakaznikName || item.suggestedZakaznikName || item.DetectedCustomerName || item.detectedCustomerName || ''

  return {
    ...item,
    localCustomerId: suggestedId || '',
    localDate: pickProtocolDate(item),
    customerSearch: initialSearch,
    localProtocolNumber: item.DetectedProtocolNumber || '',
    localAmount: item.DetectedAmount ?? '',
    localNote: item.DetectedNote || '',
    localDirty: false,
    jobCandidates: [],
    jobsChecked: false,
    selectedJob: '',
    itemError: '',
  }
}

const ScanProtokolySprava = ({ email }) => {
  const [items, setItems] = useState([])
  const [customers, setCustomers] = useState([])
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('review')
  const operationRef = useRef(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [busyId, setBusyId] = useState(null)
  const [previewItem, setPreviewItem] = useState(null)
  const [brokenPreviews, setBrokenPreviews] = useState({})
  const [activeCustomerPickerId, setActiveCustomerPickerId] = useState(null)

  const customerMap = useMemo(() => new Map(customers.map((customer) => [Number(customer.ZakaznikId), customer])), [customers])

  const loadData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [queueItems, customerItems] = await Promise.all([
        fetchScanProtocolQueue({ limit: 200 }),
        fetchCustomers(),
      ])

      const activeCustomers = customerItems.filter((customer) => Number(customer.ZakaznikId) > 0)
      const map = new Map(activeCustomers.map((customer) => [Number(customer.ZakaznikId), customer]))

      setCustomers(activeCustomers)
      setItems(queueItems.map((item) => buildInitialItem(item, map)))
      setBrokenPreviews({})
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Načtení protokolů selhalo.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])

  const pendingIds = items.filter((item) => ['queued', 'pending', 'processing'].includes(item.ProcessingStatus || item.processingStatus)).map((item) => item.Id || item.id).join(',')
  useEffect(() => {
    if (!pendingIds) return undefined
    let active = true
    let running = false
    const timer = window.setInterval(async () => {
      if (running) return
      running = true
      try {
        const rows = await fetchScanProtocolQueue({ ids: pendingIds.split(',').map(Number), limit: 200 })
        if (active) setItems((current) => current.map((item) => {
          const row = rows.find((candidate) => Number(candidate.Id || candidate.id) === Number(item.Id || item.id))
          return row && !item.localDirty ? buildInitialItem(row, customerMap) : item
        }))
      } catch (pollError) {
        if (active) setError(pollError.message || 'Obnovení fronty selhalo.')
      } finally { running = false }
    }, 5000)
    return () => { active = false; window.clearInterval(timer) }
  }, [pendingIds, customerMap])

  useEffect(() => {
    if (!previewItem) return undefined
    const onKeyDown = (event) => { if (event.key === 'Escape') setPreviewItem(null) }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [previewItem])

  const filteredItems = useMemo(() => {
    const term = normalizeText(search)
    const sorted = items.filter((item) => {
      const status = item.ProcessingStatus || item.processingStatus
      if (statusFilter === 'assigned') return Boolean(getAssignedJobId(item))
      if (statusFilter === 'review') return !getAssignedJobId(item) && status === 'processed'
      if (statusFilter === 'failed') return ['failed', 'invalid'].includes(status)
      if (statusFilter === 'pending') return ['queued', 'pending', 'processing'].includes(status)
      return true
    }).sort((left, right) => Number(right.Id || right.id || 0) - Number(left.Id || left.id || 0))
    if (!term) return sorted

    return sorted.filter((item) => {
      const selectedCustomer = customerMap.get(Number(item.localCustomerId || 0))
      const haystack = [
        buildDisplayTitle(item),
        item.SourceFileName,
        item.DetectedCustomerName,
        item.SuggestedZakaznikName,
        item.DetectedProtocolNumber,
        item.DetectedCustomerIC,
        item.DetectedCity,
        item.UserEmail,
        selectedCustomer?.Nazev,
      ].filter(Boolean).join(' | ')
      return normalizeText(haystack).includes(term)
    })
  }, [customerMap, items, search, statusFilter])

  const counts = useMemo(() => ({
    total: filteredItems.length,
    processed: filteredItems.filter((item) => normalizeText(item.ProcessingStatus) === 'processed').length,
    assigned: filteredItems.filter((item) => getAssignedJobId(item)).length,
  }), [filteredItems])

  const updateItem = (scanId, patch) => {
    setItems((prev) => prev.map((item) => (
      Number(item.Id || item.id) === Number(scanId)
        ? { ...item, localDirty: true, ...(('localCustomerId' in patch || 'localDate' in patch || 'localProtocolNumber' in patch) ? { jobsChecked: false, jobCandidates: [], selectedJob: '' } : {}), ...patch }
        : item
    )))
  }

  const handleCustomerSearchChange = (item, value) => {
    const scanId = Number(item.Id || item.id)
    const selectedCustomer = customerMap.get(Number(item.localCustomerId || 0))
    updateItem(scanId, {
      customerSearch: value,
      localCustomerId: normalizeText(value) === normalizeText(selectedCustomer?.Nazev) ? item.localCustomerId : '',
    })
  }

  const selectCustomer = (scanId, customer) => {
    updateItem(scanId, {
      localCustomerId: customer.ZakaznikId,
      customerSearch: customer.Nazev || '',
    })
    setActiveCustomerPickerId(null)
  }

  const handleAssign = async (item) => {
    if (operationRef.current) return
    const scanId = Number(item.Id || item.id)
    const zakaznikId = Number(item.localCustomerId || 0)
    const datum = pickProtocolDate(item)

    if (!scanId || !zakaznikId || !datum || !item.jobsChecked || !item.selectedJob) {
      updateItem(scanId, { itemError: 'Vyber zákazníka, datum a cílovou zakázku.' })
      return
    }

    operationRef.current = true
    setBusyId(scanId)
    setError('')
    try {
      const result = await assignScanProtocolToJob({ scanId, zakaznikId, datum, targetJobId: item.selectedJob === 'new' ? null : Number(item.selectedJob), createNew: item.selectedJob === 'new', corrections: { protocolNumber: item.localProtocolNumber, amount: item.localAmount, note: item.localNote } })
      const updated = result && result.scan ? result.scan : null
      if (!updated || !getAssignedJobId(updated)) throw new Error('Server nepotvrdil přiřazení protokolu.')
      const selectedCustomer = customerMap.get(zakaznikId)
      updateItem(scanId, {
        ...(updated || {}),
        localCustomerId: zakaznikId,
        localDate: datum,
        customerSearch: selectedCustomer?.Nazev || item.customerSearch,
        localDirty: false,
        itemError: '',
      })
    } catch (assignError) {
      updateItem(scanId, { itemError: assignError instanceof Error ? assignError.message : 'Přiřazení protokolu selhalo.' })
    } finally {
      operationRef.current = false
      setBusyId(null)
    }
  }

  const handleDelete = async (item) => {
    const scanId = Number(item.Id || item.id)
    if (!scanId || operationRef.current || !window.confirm('Opravdu trvale smazat tento protokol a jeho fotografii?')) return

    operationRef.current = true
    setBusyId(scanId)
    setError('')
    try {
      await deleteScanProtocol({ scanId })
      setItems((prev) => prev.filter((row) => Number(row.Id || row.id) !== scanId))
      if (previewItem && Number(previewItem.Id || previewItem.id) === scanId) {
        setPreviewItem(null)
      }
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : 'Smazání protokolu selhalo.')
    } finally {
      operationRef.current = false
      setBusyId(null)
    }
  }

  const loadJobs = async (item) => {
    const scanId = Number(item.Id || item.id)
    updateItem(scanId, { jobsLoading: true, itemError: '' })
    try {
      const jobCandidates = await fetchScanJobCandidates({ scanId, zakaznikId: item.localCustomerId, datum: pickProtocolDate(item), protocolNumber: item.localProtocolNumber })
      setItems((current) => current.map((row) => Number(row.Id || row.id) === scanId && row.localCustomerId === item.localCustomerId && row.localDate === item.localDate && row.localProtocolNumber === item.localProtocolNumber
        ? { ...row, jobCandidates, jobsChecked: true, selectedJob: '', jobsLoading: false }
        : Number(row.Id || row.id) === scanId ? { ...row, jobsLoading: false } : row))
    } catch (jobError) {
      updateItem(scanId, { jobsLoading: false, itemError: jobError.message || 'Načtení zakázek selhalo.' })
    }
  }

  const handleRetry = async (item) => {
    if (operationRef.current) return
    const scanId = Number(item.Id || item.id)
    operationRef.current = true
    setBusyId(scanId)
    try {
      const result = await retryScanProtocol({ scanId })
      updateItem(scanId, buildInitialItem(result.scan, customerMap))
    } catch (retryError) {
      updateItem(scanId, { itemError: retryError.message || 'Opakování selhalo.' })
    } finally { operationRef.current = false; setBusyId(null) }
  }

  const getCandidates = (item) => {
    const query = normalizeText(item.customerSearch)
    const selectedId = Number(item.localCustomerId || 0)
    const suggestedId = Number(item.SuggestedZakaznikId || item.suggestedZakaznikId || 0)

    return customers
      .map((customer) => ({ customer, score: getCandidateScore(customer, query, selectedId, suggestedId) }))
      .filter(({ score }) => score > 0 || !query)
      .sort((left, right) => right.score - left.score || String(left.customer.Nazev || '').localeCompare(String(right.customer.Nazev || ''), 'cs'))
      .slice(0, 8)
      .map(({ customer }) => customer)
  }

  return (
    <div>
      <Prompt when={items.some((item) => item.localDirty)} message="Máte neuložené úpravy protokolů. Opravdu odejít?" />
      <h2 className="page-header">Správa protokolů</h2>

      <div className="crm-scan-admin-card crm-scan-admin-card--tuned">
        <div className="card__header">
          <h3>AI protokoly po zpracování</h3>
        </div>

        <div className="card__body">
          <div className="crm-toolbar crm-toolbar--scan-admin">
            <select aria-label="Stav protokolů" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <option value="review">Ke kontrole</option>
              <option value="pending">Zpracovávají se</option>
              <option value="failed">Chyby</option>
              <option value="assigned">Přiřazené</option>
              <option value="all">Všechny</option>
            </select>
            <input
              placeholder="Hledat podle zákazníka, protokolu, města nebo nahrávajícího..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <button type="button" className="btn" onClick={() => { if (!items.some((item) => item.localDirty) || window.confirm('Obnovení zahodí neuložené úpravy. Pokračovat?')) loadData() }} disabled={loading || busyId !== null}>
              <i className="bx bx-refresh" aria-hidden="true" />
              Obnovit
            </button>
            <Link className="btn btn-primary" to="/scan-protokoly">
              Zpět na scan
            </Link>
          </div>

          <div className="crm-scan-admin-banner crm-scan-admin-banner--stats">
            <span>Protokolů: {counts.total}</span>
            <span>Zpracováno: {counts.processed}</span>
            <span>Přiřazeno: {counts.assigned}</span>
            <span>Aktuální uživatel: {getUploaderLabel(email)}</span>
          </div>

          {error ? <p className="crm-error">{error}</p> : null}
          {loading ? <p className="crm-form-note">Načítám protokoly...</p> : null}
          {!loading && !error && filteredItems.length === 0 ? <p className="crm-form-note" role="status">V tomto přehledu nejsou žádné protokoly.</p> : null}

          <div className="crm-scan-admin-list">
            {filteredItems.map((item) => {
              const scanId = Number(item.Id || item.id)
              const statusCode = normalizeText(item.ProcessingStatus || item.processingStatus)
              const statusLabel = getStatusLabel(item.ProcessingStatus || item.processingStatus)
              const assignedZakazkaId = getAssignedJobId(item)
              const previewUrl = item.BlobPreviewUrl || item.blobPreviewUrl || item.BlobUrl || item.blobUrl || ''
              const selectedCustomer = customerMap.get(Number(item.localCustomerId || 0)) || null
              const suggestedCustomer = customerMap.get(Number(item.SuggestedZakaznikId || item.suggestedZakaznikId || 0)) || null
              const uploaderEmail = item.UserEmail || item.userEmail || email || ''
              const uploaderName = getUploaderLabel(uploaderEmail)
              const aiFields = [
                { label: 'Číslo protokolu', value: item.DetectedProtocolNumber || '-' },
                { label: 'Cena', value: formatMoney(item.DetectedAmount) },
                { label: 'IČO', value: item.DetectedCustomerIC || '-' },
                { label: 'Město', value: item.DetectedCity || '-' },
                { label: 'Ulice', value: item.DetectedStreet || '-' },
                { label: 'Poznámka', value: item.DetectedNote || '-' },
              ]
              const pickerActive = activeCustomerPickerId === scanId
              const candidates = pickerActive ? getCandidates(item) : []
              const dateMode = item.ScanMode || item.scanMode
              const serverCandidates = Array.isArray(item.customerCandidates) ? item.customerCandidates : []

              return (
                <article key={scanId || item.SourceFileName} className="crm-protocol-card">
                  <div className="crm-protocol-card__preview">
                    {previewUrl && !brokenPreviews[scanId] ? (
                      <img
                        src={previewUrl}
                        alt={buildDisplayTitle(item)}
                        loading="lazy"
                        decoding="async"
                        onError={() => setBrokenPreviews((prev) => ({ ...prev, [scanId]: true }))}
                      />
                    ) : (
                      <div className="crm-protocol-card__preview-fallback">
                        <i className="bx bx-image-alt" />
                        <span>Náhled není dostupný</span>
                      </div>
                    )}

                    <div className="crm-protocol-card__preview-actions">
                      <button type="button" className="btn btn-blue table-action-btn table-action-btn--compact" onClick={() => setPreviewItem(item)}>
                        Detail
                      </button>
                      {previewUrl ? (
                        <a className="btn table-action-btn table-action-btn--compact" href={previewUrl} target="_blank" rel="noreferrer">
                          Otevřít detail
                        </a>
                      ) : null}
                    </div>
                  </div>

                  <div className="crm-protocol-card__content">
                    <div className="crm-protocol-card__header">
                      <div className="crm-protocol-card__title-wrap">
                        <span className="crm-protocol-card__eyebrow">Scan #{scanId || '-'}</span>
                        <h4>{buildDisplayTitle(item)}</h4>
                      </div>
                      <span className={`crm-protocol-status crm-protocol-status--${statusCode || 'default'}`}>{statusLabel}</span>
                    </div>

                    <div className="crm-protocol-card__meta">
                      <span>{dateMode === 'same-day' ? 'Dnešní realizace: datum známé' : dateMode === 'historical' ? 'Dodatečný scan' : 'Původní scan: ověřit datum'}</span>
                      <span>Datum z textu: {formatDate(item.DetectedProtocolDate)}</span>
                      <span>Datum scanu: {formatDate(item.CapturedAt || item.capturedAt || item.CreatedAt || item.createdAt)}</span>
                      <span>Nahrál: {uploaderName}</span>
                      <span>Čas nahrání: {formatDateTime(item.CreatedAt || item.createdAt)}</span>
                      <span>Zakázka: {assignedZakazkaId ? <Link to={`/zakazky/${assignedZakazkaId}`}>#{assignedZakazkaId}</Link> : 'zatím není'}</span>
                    </div>

                    <div className="crm-protocol-card__grid">
                      <section className="crm-protocol-panel">
                        <div className="crm-protocol-panel__head">
                          <h5>AI vytěžení</h5>
                          {suggestedCustomer ? <span className="crm-protocol-chip crm-protocol-chip--accent">AI našla: {suggestedCustomer.Nazev}</span> : null}
                        </div>

                        <div className="crm-protocol-fields">
                          {aiFields.map((field) => (
                            <div key={field.label} className="crm-protocol-field">
                              <span>{field.label}</span>
                              <strong>{field.value}</strong>
                            </div>
                          ))}
                        </div>
                      </section>

                      <section className="crm-protocol-panel">
                        <div className="crm-protocol-panel__head">
                          <h5>Zákazník a zakázka</h5>
                          {selectedCustomer ? <span className="crm-protocol-chip">Vybraný: {selectedCustomer.Nazev}</span> : null}
                        </div>

                        {!assignedZakazkaId && serverCandidates.length > 0 ? <div className="crm-protocol-suggestions" aria-label="Návrhy zákazníků">
                          {serverCandidates.slice(0, 3).map((candidate) => <button type="button" className="crm-protocol-suggestion" key={candidate.ZakaznikId} onClick={() => selectCustomer(scanId, customerMap.get(Number(candidate.ZakaznikId)) || candidate)}>
                            <strong>{candidate.Nazev}</strong><span>{candidate.Ulice} {candidate.Mesto}</span>
                            <em>{candidate.confidence === 'high' ? 'Silná shoda' : 'K ověření'}: {(candidate.reasons || []).join(', ') || 'Podobnost údajů'}</em>
                          </button>)}
                        </div> : null}

                        <label className="crm-protocol-search">
                          <span>Najít aktivního zákazníka</span>
                          <input
                            type="text"
                            disabled={Boolean(assignedZakazkaId)}
                            value={item.customerSearch || ''}
                            onChange={(event) => handleCustomerSearchChange(item, event.target.value)}
                            onFocus={() => setActiveCustomerPickerId(scanId)}
                            onKeyDown={(event) => { if (event.key === 'Escape') setActiveCustomerPickerId(null) }}
                            placeholder="Začni psát název, IČO, ulici, email..."
                          />
                        </label>

                        {pickerActive ? <div className="crm-protocol-suggestions">
                          {candidates.map((customer) => {
                            const isSelected = Number(customer.ZakaznikId) === Number(item.localCustomerId || 0)
                            const isSuggested = Number(customer.ZakaznikId) === Number(item.SuggestedZakaznikId || item.suggestedZakaznikId || 0)
                            return (
                              <button
                                type="button"
                                key={customer.ZakaznikId}
                                className={`crm-protocol-suggestion${isSelected ? ' is-selected' : ''}`}
                                onClick={() => selectCustomer(scanId, customer)}
                              >
                                <strong>{customer.Nazev}</strong>
                                <span>{[customer.Ulice, customer.Mesto, customer.IC].filter(Boolean).join(' · ') || customer.Email || '-'}</span>
                                {isSuggested ? <em>AI návrh</em> : null}
                              </button>
                            )
                          })}
                          {candidates.length === 0 ? <span className="crm-protocol-admin-note">Žádný aktivní zákazník neodpovídá hledání.</span> : null}
                        </div> : null}

                        <label className="crm-protocol-search crm-protocol-search--date">
                          <span>Datum realizace zakázky</span>
                          <input
                            type="date"
                            disabled={Boolean(assignedZakazkaId)}
                            value={pickProtocolDate(item)}
                            onChange={(event) => updateItem(scanId, { localDate: event.target.value })}
                          />
                        </label>

                        {!pickProtocolDate(item) ? <p className="crm-error">Chybí datum realizace.</p> : null}
                        <div className="crm-protocol-edit-fields">
                          <label className="crm-protocol-search"><span>Číslo protokolu</span><input inputMode="numeric" value={item.localProtocolNumber} disabled={Boolean(assignedZakazkaId)} onChange={(event) => updateItem(scanId, { localProtocolNumber: event.target.value })} /></label>
                          <label className="crm-protocol-search"><span>Cena zakázky (Kč)</span><input type="number" min="0" step="0.01" value={item.localAmount} disabled={Boolean(assignedZakazkaId)} onChange={(event) => updateItem(scanId, { localAmount: event.target.value })} /></label>
                          <label className="crm-protocol-search"><span>Poznámka</span><textarea rows="2" value={item.localNote} disabled={Boolean(assignedZakazkaId)} onChange={(event) => updateItem(scanId, { localNote: event.target.value })} /></label>
                        </div>
                        {!assignedZakazkaId && statusCode === 'processed' ? <>
                          <button className="btn" type="button" disabled={!item.localCustomerId || !pickProtocolDate(item) || item.jobsLoading} onClick={() => loadJobs(item)}><i className="bx bx-search" aria-hidden="true" /> {item.jobsLoading ? 'Hledám zakázky…' : 'Vybrat cílovou zakázku'}</button>
                          {item.jobsChecked ? <fieldset className="crm-protocol-job-picker">
                            <legend>Cílová zakázka</legend>
                            {item.jobCandidates.map((job) => <label key={job.ZakazkaId}>
                              <input type="radio" name={`job-${scanId}`} value={job.ZakazkaId} checked={item.selectedJob === String(job.ZakazkaId)} disabled={Boolean(job.ProtokolBlobUrl && job.ProtokolBlobUrl !== item.BlobUrl)} onChange={(event) => updateItem(scanId, { selectedJob: event.target.value })} />
                              <span>#{job.ZakazkaId} · {formatDate(job.Datum)} · {formatMoney(job.CenaZakazky)}{job.ProtokolBlobUrl ? ' · Má protokol' : ''}</span>
                            </label>)}
                            <label><input type="radio" name={`job-${scanId}`} value="new" checked={item.selectedJob === 'new'} onChange={() => updateItem(scanId, { selectedJob: 'new' })} /><span>Vytvořit novou zakázku</span></label>
                          </fieldset> : null}
                        </> : null}
                      </section>
                    </div>

                    <div className="crm-protocol-card__actions">
                      {assignedZakazkaId ? <Link className="btn btn-blue" to={`/zakazky/${assignedZakazkaId}`}><i className="bx bx-edit" aria-hidden="true" /> Otevřít zakázku #{assignedZakazkaId}</Link> : <button type="button" className="btn btn-blue" onClick={() => handleAssign(item)} disabled={busyId !== null || !item.localCustomerId || !pickProtocolDate(item) || !item.jobsChecked || !item.selectedJob || statusCode !== 'processed'}>
                        <i className="bx bx-check" aria-hidden="true" /> {busyId === scanId ? 'Ukládám…' : item.selectedJob === 'new' ? 'Vytvořit zakázku' : 'Přiřadit protokol'}
                      </button>}
                      {['failed', 'invalid'].includes(statusCode) && !assignedZakazkaId ? <button type="button" className="btn" disabled={busyId !== null} onClick={() => handleRetry(item)}><i className="bx bx-refresh" aria-hidden="true" /> Zpracovat znovu</button> : null}
                      <button type="button" className="btn" title="Smazat protokol" aria-label="Smazat protokol" onClick={() => handleDelete(item)} disabled={busyId !== null || Boolean(assignedZakazkaId) || ['queued', 'pending', 'processing'].includes(statusCode)}>
                        <i className="bx bx-trash" aria-hidden="true" />
                      </button>
                      <span className="crm-protocol-card__tech-line">
                        Soubor: {item.SourceFileName || '-'}
                      </span>
                    </div>
                    {item.itemError || item.ProcessingError ? <p className="crm-error" role="alert">{item.itemError || item.ProcessingError}</p> : null}
                  </div>
                </article>
              )
            })}
          </div>
        </div>
      </div>

      {previewItem ? (
        <div className="crm-protocol-modal" role="dialog" aria-modal="true">
          <div className="crm-protocol-modal__backdrop" onClick={() => setPreviewItem(null)} />
          <div className="crm-protocol-modal__content">
            <div className="crm-protocol-modal__header">
              <div>
                <span className="crm-protocol-card__eyebrow">Detail protokolu</span>
                <h3>{buildDisplayTitle(previewItem)}</h3>
              </div>
              <button type="button" className="btn" onClick={() => setPreviewItem(null)}>Zavřít</button>
            </div>

            <div className="crm-protocol-modal__body">
              <div className="crm-protocol-modal__image-wrap">
                {previewItem.BlobPreviewUrl || previewItem.blobPreviewUrl || previewItem.BlobUrl || previewItem.blobUrl ? (
                  <img src={previewItem.BlobPreviewUrl || previewItem.blobPreviewUrl || previewItem.BlobUrl || previewItem.blobUrl} alt={buildDisplayTitle(previewItem)} decoding="async" />
                ) : (
                  <div className="crm-protocol-card__preview-fallback crm-protocol-card__preview-fallback--large">
                    <i className="bx bx-image-alt" />
                    <span>Náhled není dostupný</span>
                  </div>
                )}
              </div>

              <div className="crm-protocol-modal__info">
                <div className="crm-protocol-fields">
                  <div className="crm-protocol-field"><span>Datum scanu</span><strong>{formatDate(previewItem.CapturedAt || previewItem.CreatedAt)}</strong></div>
                  <div className="crm-protocol-field"><span>Nahrál</span><strong>{getUploaderLabel(previewItem.UserEmail || previewItem.userEmail || email)}</strong></div>
                  <div className="crm-protocol-field"><span>Číslo protokolu</span><strong>{previewItem.DetectedProtocolNumber || '-'}</strong></div>
                  <div className="crm-protocol-field"><span>Cena</span><strong>{formatMoney(previewItem.DetectedAmount)}</strong></div>
                  <div className="crm-protocol-field"><span>Zákazník z AI</span><strong>{previewItem.DetectedCustomerName || previewItem.SuggestedZakaznikName || '-'}</strong></div>
                  <div className="crm-protocol-field"><span>Poznámka</span><strong>{previewItem.DetectedNote || '-'}</strong></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default ScanProtokolySprava